import Alert from "@mui/material/Alert";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import Link from "@mui/material/Link";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { useState, type FormEvent } from "react";
import { Link as RouterLink } from "react-router-dom";
import { getAuthErrorMessage } from "@bw/shared/auth-errors";
import { useAuth } from "@bw/shared/hooks";

export function ForgotPasswordForm() {
  const { resetPassword } = useAuth();
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const onSubmit = async (event: FormEvent) => {
    event.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      await resetPassword?.(email);
      setSent(true);
    } catch (err) {
      setError(getAuthErrorMessage(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Paper variant="outlined" sx={{ p: { xs: 2.5, sm: 3 } }}>
      {sent ? (
        <Stack spacing={2.5}>
          <Alert severity="success" role="status">
            If an account exists for <strong>{email}</strong>, a reset link is
            on its way.
          </Alert>
          <Link
            component={RouterLink}
            to="/sign-in"
            sx={{ alignSelf: "center" }}
          >
            Back to sign in
          </Link>
        </Stack>
      ) : (
        <Stack component="form" onSubmit={onSubmit} spacing={2.25}>
          <Typography color="text.secondary" variant="body2">
            Enter your email and we will send password-reset instructions.
          </Typography>
          {error ? (
            <Alert severity="error" role="alert">
              {error}
            </Alert>
          ) : null}
          <TextField
            autoComplete="email"
            label="Email"
            onChange={(event) => setEmail(event.target.value)}
            placeholder="you@example.com"
            required
            type="email"
            value={email}
          />
          <Button
            disabled={submitting}
            fullWidth
            startIcon={
              submitting ? (
                <CircularProgress
                  aria-hidden="true"
                  color="inherit"
                  size={16}
                />
              ) : undefined
            }
            type="submit"
            variant="contained"
          >
            {submitting ? "Sending…" : "Send reset link"}
          </Button>
          <Typography variant="body2" sx={{ textAlign: "center" }}>
            Remember your password?{" "}
            <Link component={RouterLink} to="/sign-in">
              Back to sign in
            </Link>
          </Typography>
        </Stack>
      )}
    </Paper>
  );
}
